import { List } from "immutable";
import { isNil } from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import ZoomContainer from "../../components/scrollable/ZoomContainer";
import SplitView from "../../components/splitView";
import { CONTEXT_BAR_HEIGHT, TIMELINE_HEADER_HEIGHT, TIMELINE_MOBILE_HEADER_HEIGHT } from "../../constants";
import filterMatchOptions from "../../constants/filterMatchOptions";
import Dimensions, { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import timelineViewConstants from "../../constants/styles/timelineView";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import dateFunctions, { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { isTimespanZero } from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import { unitScaleForZoomLimit } from "../../modules/definition/calendar/dateFunctions/unitScaleFunctions";
import { filterSelectors } from "../../modules/filters";
import { itemSelectors } from "../../modules/items";
import { immutableRecords, PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import workViewSelectors from "../workView/selectors";
import wrapWorkView from "../workView/wrapWorkView";
import ContextBarView from "./ContextBarView";
import TimelineView from "./index";
import selectors from "./selectors";
import TimelineAgeView from "./TimelineAgeView";
import TimelineHeaderView from "./TimelineHeaderView";
import TimelineSyncScroller from "./TimelineSyncScroller";

const SCROLL_FIRST_EVENT_MARGIN = 50;

class TimelineViewLayout extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      dragHoverDate: null,
      dragHoverDateString: null,
      dragHoverScreenYPixel: null,
      a: null,
      b: null
    };
    this.updateDragHoverValues = this.updateDragHoverValues.bind(this);
    this.requestViewBoundsForZoom = this.requestViewBoundsForZoom.bind(this);
    this.onScrollChange = this.onScrollChange.bind(this);
    this.renderChildView = this.renderChildView.bind(this);
    this.renderSplitView = this.renderSplitView.bind(this);
  }

  requestViewBoundsForZoom(zoom) {
    const rect = this.props.requestXViewBoundsForZoom(zoom);
    const height = this.props.containerRect.height - CONTEXT_BAR_HEIGHT;

    return rect.merge({
      height,
      bottom: height
    });
  }

  onScrollChange(scrollPosition, zoom) {
    this.props.onScrollChange(this.props.viewIds, scrollPosition, zoom);
  }

  updateDragHoverValues = (dragHoverDate, dragHoverDateString, dragHoverScreenYPixel) => {
    this.setState({
      dragHoverDate,
      dragHoverDateString,
      dragHoverScreenYPixel
    });
  };

  renderChildView = (view, siblingCount, viewsWithOneDateItemFilter, shouldAutoScroll) => {
    const timelineView = (
      // <SmartErrorBoundary fixed={true} key={view.viewId}>
      <TimelineView
        key={view.viewId}
        viewId={view.viewId}
        onDragHoverValuesChange={this.updateDragHoverValues}
        shouldAutoScroll={shouldAutoScroll}
      />
      // </SmartErrorBoundary>
    );
    const viewWithOneDateItemFilter = viewsWithOneDateItemFilter.find(f => f.viewId === view.viewId);

    if (viewWithOneDateItemFilter.itemId) {
      const filterItem = this.props.itemsById.get(viewWithOneDateItemFilter.itemId);
      const filterItemEndDate =
        filterItem && isDateValid(filterItem.startDate) && !isTimespanZero(filterItem.duration)
          ? dateFunctions.dateByAddingSpan(this.props.calendar, filterItem.startDate, filterItem.duration)
          : null;
      const SCALED_AGE_HEIGHT = scalePixelsToFontSize(timelineViewConstants.AGE_HEIGHT, this.props.fontSize);
      const splitProps = {
        vertical: false,
        constraints: List([
          immutableRecords.SplitConstraintsRecord({
            minSize: SCALED_AGE_HEIGHT,
            maxSize: SCALED_AGE_HEIGHT,
            preferredSize: SCALED_AGE_HEIGHT
          }),
          immutableRecords.SplitConstraintsRecord({
            minSize: Math.max(view.minSize.height - SCALED_AGE_HEIGHT, 1),
            canResize: false,
            hasDivider: false,
            dividerVisible: false,
            defaultSize: Math.max(view.size.height - SCALED_AGE_HEIGHT, 1)
          })
        ])
      };

      const childComponents = [
        <TimelineAgeView
          key="timelineAge"
          viewId={view.viewId}
          dragHoverDate={this.state.dragHoverDate}
          dragHoverDateString={this.state.dragHoverDateString}
          startDate={filterItem.startDate}
          endDate={filterItemEndDate}
        />,
        timelineView
      ];

      const splitView = (
        <SplitView key="timelineLayoutSplitView" {...splitProps}>
          {childComponents}
        </SplitView>
      );

      if (siblingCount > 1) {
        return wrapWorkView(
          splitView,
          view,
          siblingCount,
          this.props.maxHorizontalViews,
          this.props.maxVerticalViews,
          this.props.layoutDepth,
          this.props.workViewHandlers,
          null,
          this.props.viewLayout.orientation
        );
      }
    }

    // If we have already wrapped scrolling, then we just need to render this in a workView
    if (siblingCount > 1) {
      return wrapWorkView(
        timelineView,
        view,
        siblingCount,
        this.props.maxHorizontalViews,
        this.props.maxVerticalViews,
        this.props.layoutDepth,
        this.props.workViewHandlers,
        null,
        this.props.viewLayout.orientation
      );
    }
    return timelineView;
  };

  renderChildren = viewsWithOneDateItemFilter => {
    const v = this.props.viewLayout;
    const children = v.children;
    const siblingCount = children.count();

    return children
      .map(v => {
        return this.renderChildView(v, siblingCount, viewsWithOneDateItemFilter, this.props.shouldAutoScroll);
      })
      .toArray();
  };

  renderSplitView = () => {
    const appStyle = {
      fontSize: `${this.props.fontSize}px`,
      lineHeight: `${Dimensions.BASE_LINE_HEIGHT}em`
    };

    let splitProps;
    const children = this.props.viewLayout.get("children");
    const viewCount = children.count();

    let SCALED_HEADER_HEIGHT = scalePixelsToFontSize(TIMELINE_HEADER_HEIGHT, this.props.fontSize);

    //#!if mobile === true
// SCALED_HEADER_HEIGHT = scalePixelsToFontSize(TIMELINE_MOBILE_HEADER_HEIGHT, this.props.fontSize);
    //#!endif

    const SCALED_AGE_HEIGHT = scalePixelsToFontSize(timelineViewConstants.AGE_HEIGHT, this.props.fontSize);
    const SCALED_CONTEXT_BAR_HEIGHT = scalePixelsToFontSize(CONTEXT_BAR_HEIGHT, this.props.fontSize);

    splitProps = {
      vertical: false,
      constraints: children.map((c, index) =>
        immutableRecords.SplitConstraintsRecord({
          minSize: c.minSize.height,
          canResize: this.props.canViewResize && index !== viewCount - 1,
          hasDivider: index !== viewCount - 1,
          dividerVisible: index !== viewCount - 1,
          defaultSize: Math.max(c.size.height, 1),
          preferredSize: c.size.height,
          canCollapse: true,
          collapseSize: Dimensions.COLLAPSE_WORKVIEW_HEIGHT
        })
      )
    };

    const childComponents = [];

    // See if we need an age bar
    const viewsWithOneDateItemFilter = children
      .map(view => {
        let datedItemId;
        const filter = filterSelectors.getFilterWithIdFromFilters(this.props.filtersById, view.viewId);
        const filterRelationships = filter.relationshipsByType.concat(filter.relationshipsByReference).toList();

        loop: for (const relationship of filterRelationships) {
          if (relationship.match === filterMatchOptions.ANY || relationship.match === filterMatchOptions.ALL) {
            for (let term of relationship.terms) {
              const item = this.props.itemsById.get(term);
              if (isDateValid(item.startDate)) {
                if (!datedItemId || datedItemId === item.id) {
                  datedItemId = item.id;
                } else {
                  datedItemId = false;
                  break loop;
                }
              }
            }
          }
        }
        return {
          viewId: view.viewId,
          itemId: datedItemId
        };
      })
      .toArray();

    let showAge = false;
    //#!if mobile !== true
    const filterItem = this.props.itemsById.get(viewsWithOneDateItemFilter[0].itemId);
    showAge = children.count() === 1 && filterItem && isDateValid(filterItem.startDate);
    //#!endif

    // Add constraints for timeline header and context bar
    if (showAge) {
      splitProps.constraints = splitProps.constraints.unshift(
        immutableRecords.SplitConstraintsRecord({
          minSize: SCALED_AGE_HEIGHT,
          maxSize: SCALED_AGE_HEIGHT,
          preferredSize: SCALED_AGE_HEIGHT
        })
      );
    }
    splitProps.constraints = splitProps.constraints.unshift(
      immutableRecords.SplitConstraintsRecord({
        minSize: SCALED_HEADER_HEIGHT,
        maxSize: SCALED_HEADER_HEIGHT,
        preferredSize: SCALED_HEADER_HEIGHT
      })
    );

    //#!if iOS !== true
    splitProps.constraints = splitProps.constraints.push(
      immutableRecords.SplitConstraintsRecord({
        minSize: SCALED_CONTEXT_BAR_HEIGHT,
        maxSize: SCALED_CONTEXT_BAR_HEIGHT,
        preferredSize: SCALED_CONTEXT_BAR_HEIGHT
      })
    );
    //#!endif

    // Header at top
    childComponents.push(
      <div key="timelineHeaderWrapper" style={appStyle}>
        <TimelineHeaderView
          key="timelineHeader"
          viewIds={this.props.viewIds}
          dragHoverDate={this.state.dragHoverDate}
          dragHoverDateString={this.state.dragHoverDateString}
        />
      </div>
    );

    if (showAge === true) {
      const filterItemEndDate =
        isDateValid(filterItem.startDate) && !isTimespanZero(filterItem.duration)
          ? dateFunctions.dateByAddingSpan(this.props.calendar, filterItem.startDate, filterItem.duration)
          : null;
      childComponents.push(
        <div key="timelineAgeWrapper" style={appStyle}>
          <TimelineAgeView
            key="timelineAge"
            viewId={this.props.viewIds.get(0)}
            dragHoverDate={this.state.dragHoverDate}
            dragHoverDateString={this.state.dragHoverDateString}
            startDate={filterItem.startDate}
            endDate={filterItemEndDate}
          />
        </div>
      );
    }

    // All our children
    const childElements = this.renderChildren(viewsWithOneDateItemFilter);
    childComponents.push(...childElements);

    //#!if iOS !== true
    // And ContextBar at bottom
    childComponents.push(
      <div key="contextBarWrapper" style={appStyle}>
        <ContextBarView key="contextBar" fontSize={this.props.fontSize} viewIds={this.props.viewIds} />
      </div>
    );
    //#!endif

    const splitView = (
      <SplitView key="timelineLayoutSplitView" {...splitProps}>
        {childComponents}
      </SplitView>
    );

    if (children.count() === 1) {
      const view = children.first();
      return wrapWorkView(
        splitView,
        view,
        1,
        this.props.maxHorizontalViews,
        this.props.maxVerticalViews,
        this.props.layoutDepth,
        this.props.workViewHandlers,
        null,
        this.props.viewLayout.orientation
      );
    }
    return splitView;
  };

  renderSyncScroller = () => {
    return (
      <TimelineSyncScroller
        key="syncScroller"
        requestViewBoundsForZoom={this.requestViewBoundsForZoom}
        viewIds={this.props.viewIds}
        dragHoverDate={this.state.dragHoverDate}
        dragHoverScreenYPixel={this.state.dragHoverScreenYPixel}
        initialScrollLeft={this.props.scrollLeft}
        initialScrollTop={this.props.scrollTop}
        onScrollChange={this.onScrollChange}
      >
        {this.renderSplitView()}
      </TimelineSyncScroller>
    );
  };

  render() {
    return (
      <ZoomContainer
        initialZoom={this.props.unitScale}
        zoomMinimum={this.props.zoomMinimum}
        zoomMaximum={this.props.zoomMaximum}
      >
        {this.props.viewLayout.children.size > 0
          ? this.props.viewLayout.size.height > Dimensions.COLLAPSE_WORKVIEW_HEIGHT
            ? this.renderSyncScroller()
            : this.renderSplitView()
          : null}
      </ZoomContainer>
    );
  }
}

const mapStateToProps = (state, props) => {
  const unitScale = appStateSelectors.getViewUnitScale(state.present, props.viewIds.get(0));
  const scrollPosition = appStateSelectors.getViewScrollPosition(state.present, props.viewIds.get(0));
  const earliestObj = workViewSelectors.getEarliestItemInGroupForView(state.present, props.viewIds.get(0));
  const earliestTs = earliestObj ? itemSelectors.getEventStartTs(earliestObj) : 0;
  const scrollLeft = scrollPosition ? scrollPosition.x : earliestTs / unitScale - SCROLL_FIRST_EVENT_MARGIN;
  const scrollTop = scrollPosition ? scrollPosition.y : 0;

  const timelineViewSettings = definitionSelectors.getTimelineViewSettings(state.present);

  const calendar = calendarSelectors.getCalendar(state.present);
  const minUnitScale = unitScaleForZoomLimit(calendar, timelineViewSettings.zoomTo, false);
  const maxUnitScale = unitScaleForZoomLimit(calendar, timelineViewSettings.zoomFrom, true);

  const actualDateFrom = selectors.getActualDateFrom(state.present);
  const actualDateTo = selectors.getActualDateTo(state.present);

  const requestXViewBoundsForZoom = zoom => {
    let left = null;
    let right = null;

    // We always want to be at least as wide as the content, so if there are date limits set
    // we need to check the actual calculate content as well.
    if (actualDateFrom || actualDateTo) {
      left = isDateValid(actualDateFrom) ? Math.floor(actualDateFrom.timestamp / zoom) : null;
      right = isDateValid(actualDateTo) ? Math.ceil(actualDateTo.timestamp / zoom) : null;
      // Becuse layoutEvents is wrapped in reselect, it is safe to call these functions
      // here as they will only access a cached result that will be calculated anyway
      props.viewLayout.children.forEach(childView => {
        const timelineData = selectors.getTimelineViewData(state.present, childView.viewId);
        const ongoingDescendantsSet = itemSelectors.getSetOfItemIdsWithOngoingDescendants(state.present);
        const topLevelItems = itemSelectors.getTopLevelItems(state.present);
        const expandedItems = appStateSelectors.getViewExpandedItems(state.present, childView.viewId);
        const fontSize = appStateSelectors.getFontSize(state.present);
        const data = selectors.layoutEvents(
          timelineData,
          childView.viewId,
          ongoingDescendantsSet,
          topLevelItems,
          expandedItems,
          zoom,
          fontSize,
          calendar,
          timelineViewSettings.showItemCard,
          timelineViewSettings.eventLayout
        );

        if (data.bounds.width > 0 && !isNil(left) && data.bounds.left < left) {
          left = Math.floor(data.bounds.left);
        }
        if (data.bounds.width > 0 && !isNil(right) && data.bounds.right > right) {
          right = Math.ceil(data.bounds.right);
        }
      });

      const BOUNDS_PADDING = 400;

      left = left ? left - BOUNDS_PADDING : left;
      right = right ? right + BOUNDS_PADDING : right;
    }

    let width = null;

    if (!isNil(left) && !isNil(right)) {
      width = right - left;
    }

    return immutableRecords.FlatRectRecord({
      left,
      right,
      width,
      top: 0,
      height: 0,
      bottom: 0
    });
  };

  return {
    unitScale,
    scrollLeft,
    scrollTop,
    fontSize: appStateSelectors.getFontSize(state.present),
    zoomMinimum: minUnitScale,
    zoomMaximum: maxUnitScale,
    requestXViewBoundsForZoom,
    itemsById: itemSelectors.getItemsById(state.present),
    calendar,
    filtersById: filterSelectors.getFiltersById(state.present)
  };
};

const readonlyActions = {
  onScrollChange: (viewIds, scrollPosition, zoom) => {
    return appStateOperations.recordScrollAndZoom(viewIds.toArray(), scrollPosition, zoom);
  }
};

const editingActions = {};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

TimelineViewLayout.propTypes = {
  viewLayout: PropTypeShapes.viewLayout,
  maxHorizontalViews: PropTypes.number.isRequired,
  maxVerticalViews: PropTypes.number.isRequired,
  canViewResize: PropTypes.bool.isRequired,
  unitScale: PropTypes.number.isRequired,
  scrollLeft: PropTypes.number.isRequired,
  scrollTop: PropTypes.number.isRequired,
  viewIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  fontSize: PropTypes.number.isRequired,
  zoomMinimum: PropTypes.number.isRequired,
  zoomMaximum: PropTypes.number.isRequired,
  onScrollChange: PropTypes.func.isRequired,
  itemsById: ImmutablePropTypes.map.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  filtersById: ImmutablePropTypes.map.isRequired,
  shouldAutoScroll: PropTypes.bool,

  // Passed through to the syncScroller
  requestXViewBoundsForZoom: PropTypes.func.isRequired,

  // How deep in the layout are we? Used to determine if we will allow splitting further
  layoutDepth: PropTypes.number.isRequired,

  // Handlers to be passed to workView
  workViewHandlers: PropTypes.object.isRequired,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(withContainerContext(TimelineViewLayout));
